import { useState, useMemo } from 'react';
import * as Utilities from 'utilities/utils';
import { Box, Stack, Typography } from '@mui/material';
import AIIconButton from 'components/AI/AIIconButton/AIIconButton';
import { useQueryClient } from '@tanstack/react-query';
import { AutoFixHigh } from '@mui/icons-material';
import Select from 'react-select';
import { AlertMessage } from 'utilities/utils';
import { AICheckButton } from 'components/AI/AIButton/AICheckButton';
import {
  getGetApiV5FilesPendingReviewQueryKey,
  useGetApiV5FilesIdTagSuggestions,
  usePutApiV5FilesIdMarkAsReviewed,
  usePutApiV5FilesIdUpdateTags,
} from 'api/v5/files/files';
import { mergeTags, TagOptionWithState } from './utils/mergeTags';
import { AITagChip, AITagsSuggestionsLoadingState } from './components';
import { mapTagSuggestions } from './utils';

const MIN_TAG_ROW_HEIGHT = '23px';

export interface AIFileTagsSuggestionsProps {
  fileId: string;
  onChangeTags: (newTags: string[]) => void;
  initialTags?: TagOption[];
  isVisible?: boolean;
}

export const AIFileTagsSuggestions = ({
  fileId,
  onChangeTags,
  initialTags = [],
  isVisible = false,
}: AIFileTagsSuggestionsProps) => {
  const [selectedTags, setSelectedTags] = useState<TagOption[]>(initialTags);

  const queryClient = useQueryClient();

  const { mutateAsync: markAsReviewed } = usePutApiV5FilesIdMarkAsReviewed();

  const addTag = (tag: TagOption) => {
    setSelectedTags((prevTags) => [...prevTags, tag]);
  };

  const removeTag = (tag: TagOption) => {
    setSelectedTags((prevTags) => prevTags.filter((t) => t.key !== tag.key));
  };

  const {
    data: aiTagsSuggestionsResponse,
    isLoading,
    refetch,
    remove,
  } = useGetApiV5FilesIdTagSuggestions(fileId, {
    query: { enabled: isVisible },
  });
  const aiTagsSuggestions = mapTagSuggestions(aiTagsSuggestionsResponse);

  const { mutateAsync: changeTags, isLoading: isChangingTags } =
    usePutApiV5FilesIdUpdateTags();

  const handleChangeTags = async () => {
    if (selectedTags && fileId) {
      try {
        await changeTags({
          id: fileId,
          data: { tags: selectedTags.map((tag) => tag.key) },
        });
        onChangeTags(selectedTags.map((tag) => tag.key));

        await markAsReviewed({ id: fileId }).catch(() => {});
      } catch (error) {
        AlertMessage('error', 'Failed to update tags', 3000);
      }

      // Invalidate the pending review query to refresh the file list
      queryClient.removeQueries({
        queryKey: getGetApiV5FilesPendingReviewQueryKey(),
      });
    }
  };

  const uploadTags = useMemo<TagOption[]>(
    () => Utilities.getStorageData()?.pref_type_options?.upload_tags || [],
    [],
  );

  // Get tags that can't be added/removed
  const tagsCantAddRem = useMemo<TagOption[]>(
    () =>
      Utilities.getStorageData()?.pref_type_options?.tags_emp_cant_addrem || [],
    [],
  );

  const tags = useMemo<TagOptionWithState[]>(
    () =>
      mergeTags(selectedTags, aiTagsSuggestions, uploadTags, tagsCantAddRem),
    [selectedTags, aiTagsSuggestions, uploadTags, tagsCantAddRem],
  );

  const hasAtLeastOneTagSelected =
    tags.filter((tag) => tag.isSelected).length > 0;
  const hasAtLeastOneSuggestedTag =
    tags.filter((tag) => tag.isSuggested).length > 0;

  return (
    <Stack mb={2} gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body1" fontWeight={600}>
          Generate AI Suggested Tags
        </Typography>
        <AIIconButton
          customIcon={<AutoFixHigh />}
          size="large"
          onClick={() => {
            remove();
            refetch();
          }}
        />
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        sx={{ minHeight: MIN_TAG_ROW_HEIGHT }}
      >
        {isLoading && <AITagsSuggestionsLoadingState />}
        {!hasAtLeastOneSuggestedTag && !isLoading && (
          <Typography variant="body1">No suggested tags available</Typography>
        )}
        {tags.map((tag) => {
          if (tag.isSuggested) {
            return (
              <AITagChip
                key={tag.key}
                label={tag.label}
                isSelected={tag.isSelected}
                isEditable={tag.isEditable}
                onDelete={() => {
                  removeTag(tag);
                }}
                onSelect={() => {
                  addTag(tag);
                }}
              />
            );
          }
          return null;
        })}
      </Stack>
      <Stack gap={2}>
        <Typography variant="body1" fontWeight={600}>
          Selected Tags ({selectedTags?.length || 0})
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{ paddingRight: '17px' }}
        >
          <Stack
            gap={2}
            direction="row"
            flexWrap="wrap"
            sx={{ minHeight: MIN_TAG_ROW_HEIGHT }}
          >
            {tags
              .filter((tag) => tag.isSelected)
              .map((tag) => (
                <AITagChip
                  key={tag.key}
                  label={tag.label}
                  isSelected
                  isEditable={tag.isEditable}
                  onDelete={() => {
                    removeTag(tag);
                  }}
                />
              ))}
            {!hasAtLeastOneTagSelected && (
              <span className="error_label">
                at least
                <b className="label-medium"> 1 </b>
                tag is mandatory
              </span>
            )}
          </Stack>
          <Box>
            <AICheckButton
              onClick={handleChangeTags}
              disabled={isChangingTags || !hasAtLeastOneTagSelected}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack gap={2}>
        <Typography variant="body1" fontWeight={600}>
          Add Tag from Library
        </Typography>
        <Select
          isDisabled={isChangingTags}
          placeholder="Search and add tags..."
          options={tags.filter((tag) => !tag.isSelected && tag.isEditable)}
          onChange={(selectedOption) => {
            if (selectedOption) {
              addTag(selectedOption);
            }
          }}
          value={null}
          menuPlacement="top"
        />
      </Stack>
    </Stack>
  );
};
